import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  OnDestroy,
  Renderer2,
  TemplateRef,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { WizTranslationManager } from '@wizeoo/akm-component-lib';
import { WizRoot, WizTranslatableComponent, WizEventManager } from '@wizeoo/akm-component-lib';
import { LegendPosition } from '../ngx-charts/common/types/legend.model';
import { Color, colorSets } from '../ngx-charts/utils/color-sets';
import { ChartConfigObj } from './config/chart-config.obj';
import { WidgetKeyPair } from '@wizeoo/akm-message-lib';
import { ALK_EVENT_TYPE, CHART_WIDGET_MODE } from '../../model/enums';
import { AlkEvent } from '../../framework/events/alk.event';
/**
 * composant de visualisation de données
 */
@Component({
  selector: 'wiz-ngx-charts',
  styleUrls: ['./wiz-ngx-charts.component.scss'],
  templateUrl: './wiz-ngx-charts.component.html', 
  encapsulation: ViewEncapsulation.None
})
export class WizNgxCharts extends WizTranslatableComponent implements AfterViewInit, OnDestroy {
  /** templates */
  @ViewChild('noTemplate', { read: TemplateRef }) no: TemplateRef<any>;
  @ViewChild('pieTemplate', { read: TemplateRef }) pie: TemplateRef<any>;
  @ViewChild('barTemplate', { read: TemplateRef }) bar: TemplateRef<any>;
  @ViewChild('hBarTemplate', { read: TemplateRef }) hBar: TemplateRef<any>;

  @ViewChild('gaugeTemplate', { read: TemplateRef }) gauge: TemplateRef<any>;
  @ViewChild('treemapTemplate', { read: TemplateRef }) treemap: TemplateRef<any>;

  @ViewChild('cardTemplate', { read: TemplateRef }) card: TemplateRef<any>;

  @ViewChild('vbarstackedTemplate', { read: TemplateRef }) vbarstacked: TemplateRef<any>;
  @ViewChild('vbargroupedTemplate', { read: TemplateRef }) vbargrouped: TemplateRef<any>;

  @ViewChild('hbarstackedTemplate', { read: TemplateRef }) hbarstacked: TemplateRef<any>;

  @ViewChild('lineTemplate', { read: TemplateRef }) line: TemplateRef<any>;

  @ViewChild('polarTemplate', { read: TemplateRef }) polar: TemplateRef<any>;


  constructor(
    protected cdref: ChangeDetectorRef,
    public elementRef: ElementRef,
    public renderer: Renderer2,
  ) {
    super(cdref, elementRef, renderer);
  }

  public currentTemplate: TemplateRef<any>; // template en cours


  @Input() public animations: boolean = false;
  @Input() public legend: boolean = false;
  @Input() public legendPosition: LegendPosition = LegendPosition.Right;
  @Input() public legendTitle: string = 'Legend';
  @Input() public scheme: any = colorSets[0];
  @Input() public tooltipDisabled: boolean = false;
  @Input() public labels: boolean = false;

  @Input() public xAxis: boolean = false;
  @Input() public yAxis: boolean = false;
  @Input() public showXAxisLabel: boolean = false;
  @Input() public xAxisLabel: string = '';
  @Input() public showYAxisLabel: boolean = false;
  @Input() public yAxisLabel: string = '';
  @Input() public showDataLabel: boolean = false;
  @Input() public yScaleMax: number = null;
  @Input() public yScaleMin: number = null;
  @Input() public trimXAxisTicks: boolean = false;
  @Input() public trimYAxisTicks: boolean = false;
  @Input() public maxXAxisTickLength: number = 100;
  @Input() public maxYAxisTickLength: number = 100;
  @Input() public barPadding: number = 0;

  @Input() public xScaleMax: number = null;
  @Input() public xScaleMin: number = null;

  @Input() public min: number = null;
  @Input() public max: number = null;
  @Input() public units: string = null;
  @Input() public bigSegments: number = null;
  @Input() public showAxis: boolean = null;
  @Input() public angleSpan: number = null;
  @Input() public showText: boolean = null;

  @Input() public dataset: any[] = []; // jeu de données du composant

  // forçage de couleur sur le numberCard
  @Input() public color: string = null;

  ngAfterViewInit() {
    super.ngAfterViewInit();
    // modification d'une valeur de filtre
    WizEventManager.addListener(this, WizRoot.root(), ALK_EVENT_TYPE.WIDGET_RESIZE, this.doSizeMe);
  }

  ngOnDestroy() {
    super.ngOnDestroy();
    WizEventManager.removeListener(this, WizRoot.root(), ALK_EVENT_TYPE.WIDGET_RESIZE);
  }

  /** forcer le resize sur modif gridster */
  private doSizeMe(e: AlkEvent): void {
    window.dispatchEvent(new Event('resize'));
    // this.cdref.markForCheck();
    // console.log('chart size', this, this.getCompoSize());
  }

  /** Défini la configuration du composant. Suffisant pour tous les compos */
  private _config: ChartConfigObj;
  @Input() public set config(value: ChartConfigObj) {
    this._config = value;
    this.buildConfig();
    this.buildChart();
    setTimeout(() => {
      this.cdref.markForCheck();
    }, 0);
  }
  public get config(): ChartConfigObj {
    return this._config;
  }

  private buildConfig(): void {
    if (this._config.params) {
      this._config.params.forEach((item: WidgetKeyPair) => {
        this[item.cod] = item.val;
        if (item.cod === 'scheme') {
          // gestion des colors
          const schemeMap: Map<string, Color> = new Map(colorSets.map((c: Color) => [c.name, c]));
          const color: Color = schemeMap.get(item.val?.toString());
          
          // console.log('chart conf', item);
          
          if (!!color) {
            this.scheme = color;
          }
        }
      });
    }
  }

  /** construction des params du chart (hors data) */
  private buildChart(): void {
    this.setTemplate(this.config.chartType); // sélection du template
    this.buildResult(this.config.chartType); // alimentation du dataset
  }

  /**
   * Calcule la taille du chart selon celle de son parent
   */
  public getCompoSize(): [number, number] {
    const parentHeight = this.elementRef.nativeElement.clientHeight;
    const parentWidth = this.elementRef.nativeElement.clientWidth;
    // console.log('compos', parentWidth, parentHeight);
    return [parentWidth, parentHeight];
  }

  /**
   * retourne le bon template
   * @param kind type de charts
   */
  public setTemplate(kind: CHART_WIDGET_MODE): void {
    const templateMap: Map<CHART_WIDGET_MODE, TemplateRef<any>> = new Map<CHART_WIDGET_MODE, TemplateRef<any>>();
    templateMap.set(CHART_WIDGET_MODE.PIE, this.pie);
    templateMap.set(CHART_WIDGET_MODE.BAR, this.bar);
    templateMap.set(CHART_WIDGET_MODE.BAR_H, this.hBar);
    templateMap.set(CHART_WIDGET_MODE.GAUGE, this.gauge);
    templateMap.set(CHART_WIDGET_MODE.TREEMAP, this.treemap);
    templateMap.set(CHART_WIDGET_MODE.CARD, this.card);

    templateMap.set(CHART_WIDGET_MODE.BAR_V_STACKED, this.vbarstacked);
    templateMap.set(CHART_WIDGET_MODE.BAR_V_GROUPED, this.vbargrouped);
    templateMap.set(CHART_WIDGET_MODE.BAR_H_STACKED, this.hbarstacked);
    templateMap.set(CHART_WIDGET_MODE.LINE, this.line);
    templateMap.set(CHART_WIDGET_MODE.MULTI_LINE, this.line);
    templateMap.set(CHART_WIDGET_MODE.POLAR, this.polar);
    this.currentTemplate = templateMap.get(kind);
  }

  /**
   * construit la collection de données
   * @param kind type de charts
   */
  private buildResult(kind: CHART_WIDGET_MODE): void {
    switch (kind) {
      case CHART_WIDGET_MODE.PIE:
      case CHART_WIDGET_MODE.BAR:
      case CHART_WIDGET_MODE.BAR_H:
      case CHART_WIDGET_MODE.GAUGE:
      case CHART_WIDGET_MODE.TREEMAP:
      case CHART_WIDGET_MODE.CARD:
        this.dataset = this.config.dataset[0].items;
        // patch RM8308, forcer le libellé sur numberCard unique
        if(!!this.legend && this.dataset?.length === 1 && !!this.legendTitle) { // on force la légende
            this.dataset[0].label = WizTranslationManager.getInstance().translateKey(this.legendTitle);
            // console.log('patch legend', this.legend, this.dataset);
        }
        break;

      case CHART_WIDGET_MODE.BAR_V_STACKED:
      case CHART_WIDGET_MODE.BAR_V_GROUPED:
      case CHART_WIDGET_MODE.BAR_H_STACKED: 
        this.dataset = this.config.dataset;
        break;
      case CHART_WIDGET_MODE.LINE:
      case CHART_WIDGET_MODE.MULTI_LINE:
      case CHART_WIDGET_MODE.POLAR:
        // case CHART_WIDGET_MODE.BUBBLE:
        this.dataset = this.config.dataset;
        
        break;
    }
  }

}
